import { AppObject } from '../types'

/**
 * Pattern UV baking
 * -----------------
 * Many production GLBs (e.g. STEP-derived bottles) ship with NO UVs, and the
 * generic cylindrical projection used for the preview winds the opposite way
 * from the Product Profile viewer's wrap shader — so a pattern comes out
 * mirrored and, on export, has no UVs to sample at all.
 *
 * This module bakes cylindrical UVs that EXACTLY match the Product Profile
 * shader's `computeOverlayUV` (see
 * components/ProductProfile/utils/drawSection/applyShaderOnMaterial.ts):
 *   axisCenter = XZ midpoint of the mesh bbox
 *   theta      = atan2(x - cx, z - cz) + logoAngleOffset
 *   u          = fract((theta + PI) / (2*PI))
 *   v          = 1 - (y - minY) / height
 * with `logoAngleOffset = -atan2(logo.x - cx, logo.z - cz)` so u = 0.5 lands on
 * `logo_main`. Baking the same math means preview == exported GLB == Product
 * Profile base map == section overlays, and the pattern's logo sits on the
 * model's logo.
 *
 * If a result still looks wrong on a given model, the two inversion knobs below
 * are the only things to flip.
 */

// Set to true only if a model still reads mirrored after matching the shader.
const MIRROR_U = false
// Set to true only if a model still reads upside-down.
const FLIP_V = false

const TWO_PI = Math.PI * 2

interface Axis {
  cx: number
  cz: number
  minY: number
  height: number
}

interface Bounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
  minZ: number
  maxZ: number
}

function boundsOf(object: AppObject): Bounds | null {
  const pos = object.mesh.attributes.position?.array
  if (!pos || pos.length < 3) return null
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  let minZ = Infinity
  let maxZ = -Infinity
  for (let i = 0; i < pos.length; i += 3) {
    minX = Math.min(minX, pos[i])
    maxX = Math.max(maxX, pos[i])
    minY = Math.min(minY, pos[i + 1])
    maxY = Math.max(maxY, pos[i + 1])
    minZ = Math.min(minZ, pos[i + 2])
    maxZ = Math.max(maxZ, pos[i + 2])
  }
  return { minX, maxX, minY, maxY, minZ, maxZ }
}

function axisOf(b: Bounds): Axis {
  return {
    cx: (b.minX + b.maxX) / 2,
    cz: (b.minZ + b.maxZ) / 2,
    minY: b.minY,
    height: Math.max(b.maxY - b.minY, 1e-6),
  }
}

function centerOf(b: Bounds): { x: number; y: number; z: number } {
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2, z: (b.minZ + b.maxZ) / 2 }
}

/** Angle offset that maps the logo's angular position to u = 0.5 (front-center). */
function logoAngleOffset(axis: Axis, logoCenter: { x: number; z: number } | null): number {
  if (!logoCenter) return 0
  return -Math.atan2(logoCenter.x - axis.cx, logoCenter.z - axis.cz)
}

/** Cylindrical UV for one point, matching the Product Profile wrap shader. */
export function cylindricalUV(x: number, y: number, z: number, axis: Axis, offset: number): [number, number] {
  const theta = Math.atan2(x - axis.cx, z - axis.cz) + offset
  let u = (theta + Math.PI) / TWO_PI
  u -= Math.floor(u) // fract
  if (MIRROR_U) u = 1 - u
  let v = 1 - (y - axis.minY) / axis.height
  if (FLIP_V) v = 1 - v
  return [u, v]
}

/**
 * Bake cylindrical pattern UVs into every object, anchored to `logo_main` so the
 * pattern's front-center aligns with the model's logo. Overwrites any existing
 * UVs on `object.mesh.attributes.uv` (which is what the preview and the export
 * fallback both read).
 */
export function applyCylindricalPatternUVs(objects: AppObject[], logo: AppObject | null): void {
  const logoBounds = logo ? boundsOf(logo) : null
  const logoCenter = logoBounds ? centerOf(logoBounds) : null

  for (const o of objects) {
    const b = boundsOf(o)
    const pos = o.mesh.attributes.position?.array
    if (!b || !pos) continue
    const axis = axisOf(b)
    const offset = logoAngleOffset(axis, logoCenter)
    const uv = new Float32Array((pos.length / 3) * 2)
    for (let i = 0, j = 0; i < pos.length; i += 3, j += 2) {
      const [u, v] = cylindricalUV(pos[i], pos[i + 1], pos[i + 2], axis, offset)
      uv[j] = u
      uv[j + 1] = v
    }
    // Record where the logo's vertical center falls in this part's UV, so a pattern
    // can center on the logo instead of the part bbox.
    if (logoCenter) {
      o.logoV = 1 - (logoCenter.y - axis.minY) / axis.height
    }
    splitCylindricalSeam(o, uv)
  }
}

/**
 * Cylindrical UV runs u from 0→1 around the axis; the triangles that straddle
 * the wrap (u jumps ~1.0→0.0) interpolate backwards across the whole texture,
 * forcing the lowest mip and drawing a dark smear at the seam. Fix it by
 * duplicating each seam triangle's low-u vertices with u+1 (RepeatWrapping then
 * samples them continuously past 1.0). The duplication is mirrored into
 * `sourceAttributes` so the exported GLB carries the same seam-free geometry.
 */
function splitCylindricalSeam(o: AppObject, uv: Float32Array): void {
  const idxArr = o.mesh.index?.array
  const pos = o.mesh.attributes.position?.array
  if (!idxArr || !pos) {
    o.mesh.attributes.uv = { array: uv }
    return
  }
  const norm = o.mesh.attributes.normal?.array

  const positions = Array.from(pos)
  const normals = norm ? Array.from(norm) : null
  const uvs = Array.from(uv)
  let vertCount = pos.length / 3

  const dupMap = new Map<number, number>() // original vertex → its u+1 duplicate
  const dupOrder: number[] = [] // creation order, to mirror into sourceAttributes
  const newIndex = new Uint32Array(idxArr.length)

  for (let t = 0; t < idxArr.length; t += 3) {
    const tri = [idxArr[t], idxArr[t + 1], idxArr[t + 2]]
    const ua = uv[tri[0] * 2]
    const ub = uv[tri[1] * 2]
    const uc = uv[tri[2] * 2]
    const spansSeam = Math.max(ua, ub, uc) - Math.min(ua, ub, uc) > 0.5

    for (let k = 0; k < 3; k++) {
      const vi = tri[k]
      if (spansSeam && uv[vi * 2] < 0.5) {
        let dup = dupMap.get(vi)
        if (dup === undefined) {
          dup = vertCount++
          positions.push(pos[vi * 3], pos[vi * 3 + 1], pos[vi * 3 + 2])
          if (normals && norm) normals.push(norm[vi * 3], norm[vi * 3 + 1], norm[vi * 3 + 2])
          uvs.push(uv[vi * 2] + 1, uv[vi * 2 + 1])
          dupMap.set(vi, dup)
          dupOrder.push(vi)
        }
        newIndex[t + k] = dup
      } else {
        newIndex[t + k] = vi
      }
    }
  }

  // No seam triangles (e.g. a flat part) — just attach the UVs.
  if (dupOrder.length === 0) {
    o.mesh.attributes.uv = { array: uv }
    return
  }

  o.mesh.attributes.position = { array: new Float32Array(positions) }
  if (normals) o.mesh.attributes.normal = { array: new Float32Array(normals) }
  o.mesh.attributes.uv = { array: new Float32Array(uvs) }
  o.mesh.index = { array: newIndex }

  // Mirror the duplication into the source (export) geometry so the exported GLB
  // has matching vertices/UVs. sourceAttributes shares vertex ordering with the
  // working mesh (the loader only applies a transform, never reorders).
  const src = o.sourceAttributes
  if (src) {
    const srcPos = Array.from(src.position)
    const srcNorm = src.normal ? Array.from(src.normal) : null
    for (const vi of dupOrder) {
      srcPos.push(src.position[vi * 3], src.position[vi * 3 + 1], src.position[vi * 3 + 2])
      if (srcNorm && src.normal) srcNorm.push(src.normal[vi * 3], src.normal[vi * 3 + 1], src.normal[vi * 3 + 2])
    }
    src.position = new Float32Array(srcPos)
    if (srcNorm) src.normal = new Float32Array(srcNorm)
    src.uv = new Float32Array(uvs)
    src.index = newIndex.slice()
  }
}

/**
 * Where `logo_main` lands in the pattern UV space of the `body` part — used by
 * the template so the "align logo here" box is exactly on the model's logo.
 * Returns UV bounds (u around 0.5 by construction) or null.
 */
export function logoUvBoundsInBody(
  body: AppObject,
  logo: AppObject | null
): { minU: number; maxU: number; minV: number; maxV: number } | null {
  const bodyBounds = boundsOf(body)
  const logoBounds = logo ? boundsOf(logo) : null
  if (!bodyBounds || !logoBounds) return null

  const axis = axisOf(bodyBounds)
  const offset = logoAngleOffset(axis, centerOf(logoBounds))

  // Project the 8 corners of the logo bbox through the body's UV mapping.
  let minU = Infinity
  let maxU = -Infinity
  let minV = Infinity
  let maxV = -Infinity
  const xs = [logoBounds.minX, logoBounds.maxX]
  const ys = [logoBounds.minY, logoBounds.maxY]
  const zs = [logoBounds.minZ, logoBounds.maxZ]
  for (const x of xs) {
    for (const y of ys) {
      for (const z of zs) {
        const [u, v] = cylindricalUV(x, y, z, axis, offset)
        minU = Math.min(minU, u)
        maxU = Math.max(maxU, u)
        minV = Math.min(minV, v)
        maxV = Math.max(maxV, v)
      }
    }
  }
  return { minU, maxU, minV, maxV }
}

/** Center of a part's geometry (model units), ignoring any user move offset. */
export function objectCenter(object: AppObject): [number, number, number] | null {
  const b = boundsOf(object)
  if (!b) return null
  return [(b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2, (b.minZ + b.maxZ) / 2]
}

/** Where a world-space Y falls in this part's pattern UV (0 = top, 1 = bottom). */
export function objectVForWorldY(object: AppObject, worldY: number): number | undefined {
  const b = boundsOf(object)
  if (!b) return undefined
  const h = b.maxY - b.minY
  return h > 0 ? 1 - (worldY - b.minY) / h : undefined
}

/** The largest part by bounding-box volume — the reference "body" for the template. */
export function findBodyObject(objects: AppObject[]): AppObject | null {
  let best: AppObject | null = null
  let bestVol = -Infinity
  for (const o of objects) {
    const b = boundsOf(o)
    if (!b) continue
    const vol = (b.maxX - b.minX) * (b.maxY - b.minY) * (b.maxZ - b.minZ)
    if (vol > bestVol) {
      bestVol = vol
      best = o
    }
  }
  return best
}

/** Circumference/height (model units) of a part, for template proportions. */
export function bodyMetrics(object: AppObject): { circumference: number; height: number } {
  const b = boundsOf(object)
  if (!b) return { circumference: 1, height: 1 }
  const radius = Math.max(b.maxX - b.minX, b.maxZ - b.minZ) / 2
  const circumference = 2 * Math.PI * radius
  const height = Math.max(b.maxY - b.minY, 1e-6)
  return { circumference: circumference || height, height }
}
